/**
 * Substitutos das janelas nativas do navegador (window.confirm / alert / prompt)
 * por modais do próprio sistema (desenhadas por `components/common/DialogHost`).
 *
 * São funções de módulo, não hooks: funcionam em qualquer lugar (handlers,
 * callbacks, efeitos) sem mexer em dependências de `useCallback`. Cada uma
 * devolve uma Promise, então no código fica:
 *
 *   if (!(await confirmDialog({ title: "Excluir meta", message: "…" }))) return;
 *
 * Diferença para o `window.confirm`: a chamada é assíncrona, então o handler
 * que a usa precisa ser `async`.
 *
 * Se o `DialogHost` não estiver montado (testes, por exemplo), cai nas janelas
 * nativas, para a ação nunca ficar travada.
 */

let host = null;

export const registerDialogHost = (fn) => {
  host = fn;
  return () => {
    if (host === fn) host = null;
  };
};

const open = (descriptor) =>
  new Promise((resolve) => {
    host({ ...descriptor, resolve });
  });

const plainText = (...parts) =>
  parts
    .filter((part) => typeof part === "string" && part.trim())
    .join("\n\n");

/**
 * Pergunta com dois botões. Resolve `true` (confirmou) ou `false` (cancelou,
 * Esc ou clique fora).
 *
 * - `title`        título curto na faixa de cima.
 * - `message`      texto principal.
 * - `subject`      nome do item afetado, em destaque (ex.: nome da meta).
 * - `note`         aviso extra em caixa amarela (ex.: "não pode ser desfeita").
 * - `tone`         "danger" (excluir/remover), "warning" (padrão) ou "info".
 * - `confirmLabel` / `cancelLabel` textos dos botões.
 */
export const confirmDialog = (options = {}) => {
  if (!host) {
    return Promise.resolve(
      window.confirm(
        plainText(options.title, options.message, options.subject, options.note),
      ),
    );
  }
  return open({ kind: "confirm", ...options });
};

/**
 * Aviso com um botão. Resolve quando a pessoa fecha.
 *
 * - `tone`  "error" (padrão), "warning", "info" ou "success".
 * - `buttonLabel` texto do botão (padrão "Entendi").
 */
export const alertDialog = (options = {}) => {
  if (!host) {
    window.alert(plainText(options.title, options.message, options.note));
    return Promise.resolve();
  }
  return open({ kind: "alert", ...options });
};

/**
 * Caixa para digitar um texto. Resolve a string digitada ou `null` se cancelou.
 *
 * - `label`, `placeholder`, `defaultValue`, `multiline`, `inputType`
 * - `required`  impede confirmar vazio.
 * - `validate(valor)` devolve uma mensagem de erro (string) ou vazio se está ok.
 */
export const promptDialog = (options = {}) => {
  if (!host) {
    return Promise.resolve(
      window.prompt(
        plainText(options.title, options.message, options.label),
        options.defaultValue ?? "",
      ),
    );
  }
  return open({ kind: "prompt", ...options });
};
